import type { ReactNode } from "react";
import { ArrowRight, Lightbulb, Rocket } from "lucide-react";
import { Link } from "react-router";

/**
 * Building blocks for knowledge-base articles - shared typography, deep
 * links into the app, cross-links between articles, and callouts, so every
 * article reads the same way.
 */

export interface HelpArticleDef {
  slug: string;
  title: string;
  category:
    | "Getting started"
    | "Everyday selling"
    | "Marketing & automation"
    | "Reports & insight"
    | "Administration";
  /** Approximate read time, minutes. */
  minutes: number;
  /** One-sentence index-card summary. */
  summary: string;
  /** Extra search terms beyond title/summary. */
  keywords?: string[];
  /** Slugs of related articles, shown at the bottom. */
  related: string[];
  body: ReactNode;
}

export const HELP_CATEGORIES = [
  "Getting started",
  "Everyday selling",
  "Marketing & automation",
  "Reports & insight",
  "Administration",
] as const;

/** Section heading inside an article. */
export const Sect = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="flex flex-col gap-2.5">
    <h2 className="text-base font-semibold mt-2">{title}</h2>
    {children}
  </section>
);

/** Body paragraph. */
export const P = ({ children }: { children: ReactNode }) => (
  <p className="text-sm leading-6 text-foreground/90">{children}</p>
);

/** Ordered step list - the "do this, then this" spine of how-to sections. */
export const Steps = ({ items }: { items: ReactNode[] }) => (
  <ol className="flex flex-col gap-2 my-1">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3 text-sm leading-6 text-foreground/90">
        <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold flex items-center justify-center shrink-0 mt-0.5">
          {i + 1}
        </span>
        <span>{item}</span>
      </li>
    ))}
  </ol>
);

/** Deep link into an app screen, styled so it reads as "go here". */
export const AppLink = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link
    to={to}
    className="inline-flex items-center gap-0.5 text-primary underline underline-offset-2 hover:no-underline"
  >
    {children}
    <ArrowRight className="w-3 h-3" />
  </Link>
);

/** Cross-link to another article. */
export const ArticleLink = ({ slug, children }: { slug: string; children: ReactNode }) => (
  <Link to={`/help/${slug}`} className="underline underline-offset-2 hover:no-underline">
    {children}
  </Link>
);

/** Amber callout for what changes at deployment vs. this preview. */
export const DeploymentNote = ({ children }: { children: ReactNode }) => (
  <div className="flex gap-2.5 rounded-md border border-[#d9b36b]/50 bg-[#d9b36b]/10 px-3 py-2.5 text-sm leading-6">
    <Rocket className="w-4 h-4 shrink-0 mt-1 text-[#a8842f]" />
    <span className="text-foreground/90">{children}</span>
  </div>
);

/** Neutral tip callout. */
export const Tip = ({ children }: { children: ReactNode }) => (
  <div className="flex gap-2.5 rounded-md border border-border bg-muted/40 px-3 py-2.5 text-sm leading-6">
    <Lightbulb className="w-4 h-4 shrink-0 mt-1 text-muted-foreground" />
    <span className="text-foreground/90">{children}</span>
  </div>
);

/** Inline UI label, e.g. a button or field name. */
export const Ui = ({ children }: { children: ReactNode }) => (
  <span className="font-medium">{children}</span>
);
